import { StringDevUIField } from "./StringDevUIField";

const HOLD_DELAY_MS = 320;
const HOLD_INTERVAL_MS = 60;
const DRAG_PIXELS_PER_STEP = 6;

export class StringDevFieldRange extends StringDevUIField<number> {
  private min: number;
  private max: number;
  private step: number;
  private units: ReadonlyArray<{ value: string; label: string }> | undefined;
  private currentUnit: string | undefined;
  private valueInput: HTMLInputElement | null = null;
  private unitSelect: HTMLSelectElement | null = null;

  constructor(
    key: string,
    label: string,
    defaultValue: number,
    onChange: (key: string, value: any) => void,
    min: number = 0,
    max: number = 100,
    step: number = 1,
    units?: ReadonlyArray<{ value: string; label: string }>,
    currentUnit?: string,
  ) {
    super(key, label, defaultValue, onChange);
    this.min = min;
    this.max = max;
    this.step = step;
    this.units = units;
    this.currentUnit = currentUnit;
  }

  build(): HTMLElement {
    const row = this.createRow();
    row.appendChild(this.createLabel());

    const right = document.createElement("div");
    right.setAttribute("data-string-dev-field-range-right", "");
    right.appendChild(this.createControls());

    row.appendChild(right);
    this.root = row;
    return row;
  }

  setValue(value: number): void {
    this.value = value;
    if (this.valueInput) this.valueInput.value = this.formatValue(value);
  }

  private createControls(): HTMLDivElement {
    const control = document.createElement("div");
    control.setAttribute("data-string-dev-range-input", "");
    control.setAttribute("data-has-unit", String(!!this.units?.length));

    const valueWrap = document.createElement("div");
    valueWrap.setAttribute("data-string-dev-input-value-wrap", "");
    valueWrap.appendChild(this.createInput());
    control.appendChild(valueWrap);

    if (this.units && this.units.length > 0) {
      const unitWrap = document.createElement("div");
      unitWrap.setAttribute("data-string-dev-input-unit-wrap", "");

      const unitSelect = document.createElement("select");
      unitSelect.setAttribute("data-string-dev-field-unit", "");

      for (const u of this.units) {
        const opt = document.createElement("option");
        opt.value = u.value;
        opt.textContent = u.label;
        unitSelect.appendChild(opt);
      }

      unitSelect.value = this.currentUnit || this.units[0].value;
      unitSelect.addEventListener("change", () => {
        this.currentUnit = unitSelect.value;
        this.onChange(this.key + "Unit", unitSelect.value);
      });

      this.unitSelect = unitSelect;
      unitWrap.appendChild(unitSelect);
      control.appendChild(unitWrap);
    }

    const stepper = document.createElement("div");
    stepper.setAttribute("data-string-dev-stepper-stack", "");
    stepper.appendChild(this.createStepperButton("up", 1));
    stepper.appendChild(this.createStepperButton("down", -1));
    control.appendChild(stepper);

    return control;
  }

  private createInput(): HTMLInputElement {
    const valueInput = document.createElement("input");
    valueInput.type = "text";
    valueInput.inputMode = "decimal";
    valueInput.value = this.formatValue(this.value);
    valueInput.setAttribute("data-string-dev-field-value", "");

    valueInput.addEventListener("focus", () => {
      valueInput.select();
    });
    valueInput.addEventListener("blur", () => {
      this.commitInputValue(valueInput);
    });
    valueInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        valueInput.blur();
      } else if (e.key === "Escape") {
        valueInput.value = this.formatValue(this.value);
        valueInput.blur();
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        this.applyDelta(e.key === "ArrowUp" ? 1 : -1, e, valueInput);
      }
    });
    valueInput.addEventListener("wheel", (event) => {
      if (document.activeElement !== valueInput) return;
      event.preventDefault();
      this.applyDelta(event.deltaY < 0 ? 1 : -1, event, valueInput);
    });
    valueInput.addEventListener("mousedown", (event) => {
      if (event.button !== 1) return;
      this.startMiddleDrag(event, valueInput);
    });
    valueInput.addEventListener("auxclick", (event) => {
      if (event.button === 1) event.preventDefault();
    });

    this.valueInput = valueInput;
    return valueInput;
  }

  private createStepperButton(
    directionName: "up" | "down",
    direction: 1 | -1,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-string-dev-stepper-btn", "");
    button.setAttribute("data-direction", directionName);

    const icon = document.createElement("span");
    icon.setAttribute("data-string-dev-stepper-icon", "");
    icon.innerHTML =
      directionName === "up"
        ? `<svg viewBox="0 0 12 12" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 7L6 5L9 7" fill="currentColor"/><path d="M3 7L6 5L9 7" fill="none" stroke="currentColor"/></svg>`
        : `<svg viewBox="0 0 12 12" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5L6 7L9 5" fill="currentColor"/><path d="M3 5L6 7L9 5" fill="none" stroke="currentColor"/></svg>`;
    button.appendChild(icon);

    let holdTimeout = 0;
    let holdInterval = 0;
    let suppressClick = false;

    const stopHold = () => {
      window.clearTimeout(holdTimeout);
      window.clearInterval(holdInterval);
      document.removeEventListener("pointerup", stopHold);
      document.removeEventListener("pointercancel", stopHold);
    };

    button.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;

      holdTimeout = window.setTimeout(() => {
        suppressClick = true;
        if (!this.valueInput) return;
        this.applyDelta(direction, undefined, this.valueInput);
        holdInterval = window.setInterval(() => {
          if (!this.valueInput) return;
          this.applyDelta(direction, undefined, this.valueInput);
        }, HOLD_INTERVAL_MS);
      }, HOLD_DELAY_MS);

      document.addEventListener("pointerup", stopHold);
      document.addEventListener("pointercancel", stopHold);
    });

    button.addEventListener("click", (event) => {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        return;
      }

      if (!this.valueInput) return;
      this.applyDelta(direction, undefined, this.valueInput);
      this.valueInput.focus();
      this.valueInput.select();
    });

    return button;
  }

  private startMiddleDrag(
    event: MouseEvent,
    input: HTMLInputElement,
  ): void {
    event.preventDefault();

    let previousX = event.clientX;
    let accumulated = 0;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    const stopDrag = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", stopDrag);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - previousX;
      previousX = moveEvent.clientX;
      accumulated += deltaX;

      while (Math.abs(accumulated) >= DRAG_PIXELS_PER_STEP) {
        const direction = accumulated > 0 ? 1 : -1;
        this.applyDelta(direction, moveEvent, input);
        accumulated -= DRAG_PIXELS_PER_STEP * direction;
      }
    };

    input.focus();
    input.select();
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", stopDrag);
  }

  private commitInputValue(input: HTMLInputElement): void {
    const parsed = parseFloat(input.value.replace(",", "."));
    if (isNaN(parsed)) {
      input.value = this.formatValue(this.value);
      return;
    }
    const snapped = this.normalizeValue(parsed);
    input.value = this.formatValue(snapped);
    this.emit(snapped);
  }

  private applyDelta(
    direction: 1 | -1,
    event: KeyboardEvent | WheelEvent | MouseEvent | undefined,
    input: HTMLInputElement,
  ): void {
    const multiplier = event?.shiftKey ? 10 : event?.altKey ? 0.1 : 1;
    const nextValue = this.normalizeValue(
      this.value + direction * this.step * multiplier,
    );
    input.value = this.formatValue(nextValue);
    this.emit(nextValue);
  }

  private normalizeValue(value: number): number {
    const snapped = Math.round(value / this.step) * this.step;
    const precision = this.getPrecision(this.step);
    return Number(snapped.toFixed(precision));
  }

  private getPrecision(value: number): number {
    const parts = String(value).split(".");
    return parts[1]?.length ?? 0;
  }

  private formatValue(v: number): string {
    const precision = this.step < 1 ? Math.max(2, this.getPrecision(this.step)) : 0;
    return precision > 0 ? v.toFixed(precision) : String(v);
  }
}
